import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Package, Clock, CheckCircle2, XCircle, 
  AlertTriangle, Send, Trash2, FileText, Calendar, User
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/AuthContext';
import { stockRequestsAPI } from '@/services/api';
import { formatDate, formatDateOnly } from '@/utils/dateFormat';

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: FileText },
  pending: { label: 'Pending Approval', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  partial: { label: 'Partially Approved', color: 'bg-blue-100 text-blue-700', icon: AlertTriangle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500', icon: XCircle },
  fulfilled: { label: 'Fulfilled', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 }
};

const priorityConfig = {
  normal: { label: 'Normal', color: 'bg-gray-100 text-gray-600' },
  high: { label: 'High', color: 'bg-amber-100 text-amber-700' },
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-700' }
};

const StockRequestDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const fetchRequest = useCallback(async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const response = await stockRequestsAPI.get(id);
      setRequest(response.data);
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to fetch stock request', 
        variant: 'destructive' 
      });
      navigate('/warehouse/stock-requests');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, toast]);

  useEffect(() => {
    fetchRequest();
  }, [fetchRequest]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await stockRequestsAPI.submit(id);
      toast({ title: 'Success', description: 'Stock request submitted to HQ' });
      fetchRequest();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to submit request', 
        variant: 'destructive' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await stockRequestsAPI.delete(id);
      toast({ title: 'Success', description: 'Stock request cancelled' });
      navigate('/warehouse/stock-requests');
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to cancel request', 
        variant: 'destructive' 
      });
    }
    setDeleteDialogOpen(false);
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="p-6 lg:p-8 bg-gray-50 min-h-screen flex flex-col items-center justify-center">
        <FileText className="w-16 h-16 text-gray-300 mb-4" />
        <p className="text-gray-500">Stock request not found</p>
        <Button 
          variant="outline" 
          className="mt-4"
          onClick={() => navigate('/warehouse/stock-requests')}
        >
          Back to List
        </Button>
      </div>
    );
  }

  const StatusIcon = statusConfig[request.status]?.icon || FileText;
  const canSubmit = request.status === 'draft' && request.total_items > 0;
  const canDelete = ['draft', 'pending'].includes(request.status);

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen" data-testid="stock-request-detail-page">
      <Toaster />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/warehouse/stock-requests')}
            className="text-gray-600"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">{request.request_number}</h1>
            <p className="text-gray-500">Stock Request Details</p>
          </div>
        </div>
        <div className="flex gap-2">
          {canSubmit && (
            <Button 
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-teal-500 hover:bg-teal-600 text-white"
              data-testid="submit-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {submitting ? 'Submitting...' : 'Submit to HQ'}
            </Button>
          )}
          {canDelete && (
            <Button 
              variant="outline"
              onClick={() => setDeleteDialogOpen(true)}
              className="border-red-300 text-red-600 hover:bg-red-50"
              data-testid="cancel-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Cancel Request
            </Button>
          )}
        </div>
      </div>

      {/* Status & Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${statusConfig[request.status]?.color?.split(' ')[0] || 'bg-gray-100'}`}>
                <StatusIcon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Status</p>
                <Badge className={statusConfig[request.status]?.color}>
                  {statusConfig[request.status]?.label || request.status}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Items</p>
                <p className="text-lg font-bold text-gray-800">{request.total_items}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <Calendar className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Required By</p>
                <p className="text-sm font-medium text-gray-800">
                  {request.required_by_date ? formatDateOnly(request.required_by_date) : 'Not specified'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Created</p>
                <p className="text-sm font-medium text-gray-800">
                  {formatDate(request.created_at)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Request Info */}
      <Card className="bg-white border-gray-200 shadow-sm mb-6">
        <CardHeader className="pb-4 border-b border-gray-100">
          <CardTitle className="text-lg text-gray-800">Request Information</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-500">Warehouse</p>
              <p className="font-medium text-gray-800">{request.warehouse_name}</p>
            </div>
            {request.submitted_at && (
              <div>
                <p className="text-sm text-gray-500">Submitted At</p>
                <p className="font-medium text-gray-800">{formatDate(request.submitted_at)}</p>
              </div>
            )}
            {request.approved_at && (
              <div>
                <p className="text-sm text-gray-500">Approved At</p>
                <p className="font-medium text-gray-800">{formatDate(request.approved_at)}</p>
              </div>
            )}
            {request.approved_by_name && (
              <div>
                <p className="text-sm text-gray-500">Approved By</p>
                <p className="font-medium text-gray-800">{request.approved_by_name}</p>
              </div>
            )}
            {request.notes && (
              <div className="md:col-span-3">
                <p className="text-sm text-gray-500">Notes</p>
                <p className="font-medium text-gray-800">{request.notes}</p>
              </div>
            )}
            {request.rejection_reason && (
              <div className="md:col-span-3">
                <p className="text-sm text-red-500">Rejection Reason</p>
                <p className="font-medium text-red-700 bg-red-50 p-3 rounded-lg">
                  {request.rejection_reason}
                </p>
              </div>
            )}
            {request.linked_po_number && (
              <div>
                <p className="text-sm text-gray-500">Linked PO</p>
                <p className="font-medium text-teal-600">{request.linked_po_number}</p>
              </div>
            )}
            {request.linked_grn_number && (
              <div>
                <p className="text-sm text-gray-500">Linked GRN</p>
                <p className="font-medium text-teal-600">{request.linked_grn_number}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Items Table */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardHeader className="pb-4 border-b border-gray-100">
          <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
            <Package className="w-5 h-5 text-teal-500" />
            Requested Items ({request.items?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {!request.items || request.items.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>No items in this request</p>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-gray-600">Product</TableHead>
                    <TableHead className="text-gray-600 text-center">Requested Qty</TableHead>
                    <TableHead className="text-gray-600 text-center">Approved Qty</TableHead>
                    <TableHead className="text-gray-600 text-center">UOM</TableHead>
                    <TableHead className="text-gray-600 text-center">Priority</TableHead>
                    <TableHead className="text-gray-600">Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {request.items.map((item, index) => (
                    <TableRow key={item.id || index} className="border-gray-200">
                      <TableCell>
                        <p className="font-medium text-gray-800">{item.product_name}</p>
                        <p className="text-xs text-teal-600">{item.product_sku}</p>
                      </TableCell>
                      <TableCell className="text-center font-medium text-gray-800">
                        {item.requested_qty}
                      </TableCell>
                      <TableCell className="text-center">
                        {request.status === 'approved' || request.status === 'partial' ? (
                          <span className={`font-medium ${item.approved_qty > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {item.approved_qty || 0}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center text-gray-600">
                        {item.uom}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={priorityConfig[item.priority || 'normal']?.color}>
                          {priorityConfig[item.priority || 'normal']?.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-gray-600 text-sm">
                        {item.notes || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Stock Request?</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel the stock request &quot;{request.request_number}&quot;. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300">No, Keep It</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Yes, Cancel Request
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StockRequestDetailPage;
